import { type Dispatch, type RefObject, type SetStateAction, useCallback } from "react";

/**
 * A piece of chat state that only means anything inside one thread — the prompt a run is being
 * tracked for, the run id the controls act on.
 *
 * LAT-990: both Claudia surfaces used to hold these as plain `string | null` and clear them from
 * an effect when the thread changed. An effect is one render too late. The thread on screen
 * changes *during* render, and the transcript subscriptions are computed from these values in that
 * same render, so between the route change and the effect the page asks the new thread for the
 * previous thread's prompt id — which the server then answered, rendering another conversation's
 * message inside this one. Carrying the owning thread with the value makes the stale read
 * impossible instead of merely brief: the reader hands back `null` for any thread but the one the
 * value was written under.
 */
export type ThreadScopedValue = {
  threadId: string | null;
  value: string;
};

export function readThreadScopedValue(
  scoped: ThreadScopedValue | null,
  threadId: string | null
): string | null {
  return scoped && scoped.threadId === threadId ? scoped.value : null;
}

/**
 * A `string | null` setter over thread-scoped state, stamping whichever thread is current.
 *
 * `threadIdRef` is a render-assigned ref rather than a value so the setter identity is stable —
 * it is handed to the composer, which keys callbacks on it.
 */
export function useThreadScopedSetter(
  setScoped: Dispatch<SetStateAction<ThreadScopedValue | null>>,
  threadIdRef: RefObject<string | null>
): Dispatch<SetStateAction<string | null>> {
  return useCallback(
    (action: SetStateAction<string | null>) => {
      setScoped((current) => {
        const threadId = threadIdRef.current;
        const previous = current && current.threadId === threadId ? current.value : null;
        const next = typeof action === "function" ? action(previous) : action;
        if (next === null) {
          // Clearing is unconditional: these name what is happening *now*, and nothing wants the
          // other thread's stale pointer kept alive behind this one.
          return null;
        }
        return { threadId, value: next };
      });
    },
    [setScoped, threadIdRef]
  );
}
